/* eslint-disable no-param-reassign,func-names */
import { useProperty, useSiteContext } from '@eti/providers';
import { useEffect, useState } from 'react';
import { getTravelerPricesTotal } from '../../utils/travelers';

const initSkyscannerTag = (id) => {
  if (document.getElementById(id)) {
    return;
  }

  (function (s, k, y, t) {
    s.SkyscannerAnalyticsTag = t;
    s[t] =
      s[t] ||
      function () {
        // eslint-disable-next-line prefer-rest-params
        (s[t].buffer = s[t].buffer || []).push(arguments);
      };
    s[t].u = y;
    const l = k.createElement('script');
    l.src = `${y}/tag.js`;
    l.async = 1;
    l.id = id;
    const h = k.getElementsByTagName('head')[0];
    h.appendChild(l);
  })(window, document, 'https://analytics.skyscanner.net', 'sat');
};

const getPriceSeenData = (dataLayer) => dataLayer?.find((item) => item.priceSeen);

const shouldTrackLanding = (satId, priceData, sentPriceSeen) => {
  if (!satId || parseInt(priceData?.priceSeen, 10) === parseInt(sentPriceSeen, 10)) {
    return false;
  }

  return priceData?.priceSeen > 0;
};

export const getOrderData = (props, currencyCode, exponent) => {
  const { paymentTypes, obfuscatedOrderReference, totalPrice, travelerPrices } = props;

  const totalTicketValue = getTravelerPricesTotal(travelerPrices);
  const cardType = paymentTypes?.map((p) => p.cardSubType)[0];

  return {
    totalPrice: totalPrice / 10 ** exponent,
    totalTicketValue: totalTicketValue / 10 ** exponent,
    currency: currencyCode,
    cardType: cardType ?? '',
    bookingReference: obfuscatedOrderReference,
  };
};

export const useSkyscannerOrderTracking = (props) => {
  const { partner, siteCurrency } = useSiteContext();
  const { p } = useProperty();

  const satId = p('IbeClient.SATId');
  const isEnabled = p('IbeClient.SATPurchaseTracking.Enabled');
  const isSkyscannerPartner = partner === 'skyscanner';
  const { isCreatingOrder, isPostBookingOrder } = props;

  const eligibleForTracking =
    isEnabled &&
    isSkyscannerPartner &&
    Boolean(satId) &&
    Boolean(isCreatingOrder) &&
    !isPostBookingOrder;

  const [trackingHasBeenSent, setTrackingHasBeenSent] = useState(false);

  if (eligibleForTracking && !trackingHasBeenSent) {
    const conversionData = getOrderData(props, siteCurrency.code, siteCurrency.exponent);
    initSkyscannerTag('skyscanner-script-tag');
    if (typeof window.sat === 'function') {
      window.sat('init', satId);
      window.sat('send', 'conversion', conversionData);
    }
    setTrackingHasBeenSent(true);
  }
};

export const useSkyscannerLandingTracking = (selectedTripData) => {
  const { partner, siteCurrency } = useSiteContext();
  const { p } = useProperty();

  const satId = p('IbeClient.SATId');
  const isEnabled = p('IbeClient.SATEventTracking.Enabled');
  const isSkyscannerPartner = partner === 'skyscanner';
  const alreadySentPriceSeen = sessionStorage.getItem('sst');

  useEffect(() => {
    const priceData = getPriceSeenData(window.dataLayer);

    if (
      isEnabled &&
      isSkyscannerPartner &&
      shouldTrackLanding(satId, priceData, alreadySentPriceSeen)
    ) {
      initSkyscannerTag('skyscanner-script-tag');
      if (typeof window.sat === 'function') {
        window.sat('init', satId);
        window.sat('send', 'landing', {
          priceSeen: priceData.priceSeen,
          currency: siteCurrency.code,
          paymentOption: '',
        });
        sessionStorage.setItem('sst', priceData.priceSeen);
      }
    }
  }, [
    satId,
    isEnabled,
    isSkyscannerPartner,
    selectedTripData,
    siteCurrency.code,
    alreadySentPriceSeen,
  ]);
};
